import { useCallback } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { ImagePlaceholder } from '@lyearn/icons';
import { HotspotType } from '@lyearn/lygo-blocks';
import { Stack } from '@lyearn/ui';
import ImageWithHotspots from '@/components/ImageWithHotspots';
import UploadButton from '@/modules/classrooms/modules/assignments/homework/cards/components/UploadButton';
import AddCoverDialog from '@/modules/corporate/components/AddCoverDialog';
import { CoverType } from '@/modules/corporate/components/ImageList/types';
import { MediaKindEnum } from '@/types/schema';

import { ImageHotspotUploaderProps } from './types';

const ImageHotspotUploader: React.FC<ImageHotspotUploaderProps> = (props) => {
  const { question, onChangeQuestion, index } = props;
  const media = question.medias?.[0];

  const { t } = useTranslation();
  const [showCoverDialog, { on, off }] = useToggle(false);

  const onSubmit = useCallback(
    (selectedCard?: CoverType) => {
      if (!selectedCard?.url) {
        onChangeQuestion(index, {
          ...question,
          medias: [],
          imageHotspots: [],
        });
        return;
      }

      const { url, name } = selectedCard;
      onChangeQuestion(index, {
        ...question,
        medias: [{ url, name, kind: MediaKindEnum.Image }],
        imageHotspots: [],
      });
    },
    [index, onChangeQuestion, question],
  );

  const onChangeHotspots = useCallback(
    (hotspotsState: HotspotType[]) => {
      onChangeQuestion(index, {
        ...question,
        imageHotspots: [
          {
            hotspots: hotspotsState.map(({ x, y, id }) => ({ x, y, id })),
            mediaId: media?.mediaId,
          },
        ],
      });
    },
    [index, media?.mediaId, onChangeQuestion, question],
  );

  return (
    <>
      <Stack className="w-full pe-4" direction="col">
        {media ? (
          <ImageWithHotspots
            noCaption
            hotspots={question.imageHotspots?.[0]?.hotspots}
            imageClassName="h-[31rem]"
            media={media}
            readOnly={false}
            onChange={onChangeHotspots}
          />
        ) : (
          <UploadButton
            PlaceholderIcon={ImagePlaceholder}
            isDisabled={false}
            placeholder={t('Exercise:Creator.UploadImageHotspot')}
            supportedMimeTypes=""
            onClick={on}
          />
        )}
      </Stack>
      {showCoverDialog ? <AddCoverDialog cover={media} onClose={off} onSubmit={onSubmit} /> : null}
    </>
  );
};

export default ImageHotspotUploader;
